import { useEffect, useMemo, useRef, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { BC_CONFIG } from "./authConfig";
import { useBCData } from "./hooks/useBCData";
import { OrderDetail } from "./components/OrderDetail";
import { SwipeableOrderCard } from "./components/SwipeableOrderCard";
import { summarizeWarehouse } from "./utils/warehouse";
import "./styles/app.css";

const UNDO_WINDOW_MS = 2000;

// "Production" → PROD badge (subtle). Anything else (Sandbox, SBX_*, Test...)
// gets a loud SBX badge so it's never ambiguous which BC instance is wired.
const ENV_IS_PROD = BC_CONFIG.environment === "Production";
const ENV_BADGE = ENV_IS_PROD ? "PROD" : "SBX";

function AdelanteLogo() {
  const [imgFailed, setImgFailed] = useState(false);
  if (!imgFailed) {
    return (
      <img
        src={`${process.env.PUBLIC_URL}/logo.svg`}
        alt="Adelante"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-label="Adelante">
      <polygon points="4,16 22,16 32,48 14,48" fill="#ADD010" />
      <polygon points="18,16 36,16 46,48 28,48" fill="#ADD010" />
      <polygon points="18,16 22,16 32,48 28,48" fill="#7C9C00" />
    </svg>
  );
}

// BC stores some currencies under non-ISO codes (e.g. EURO, COLONES).
// Map them to ISO 4217 so Intl.NumberFormat doesn't throw.
const CURRENCY_ALIASES = { EURO: "EUR", COLONES: "CRC" };

const fmt = (n, currency = "CRC") => {
  if (n == null) return "—";
  const code = CURRENCY_ALIASES[currency] || currency || "CRC";
  try {
    return new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    const num = new Intl.NumberFormat("es-CR", { maximumFractionDigits: 0 }).format(n);
    return `${currency || ""} ${num}`.trim();
  }
};

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  // BC returns "0001-01-01" for empty date fields. Treat as null.
  if (isNaN(date.getTime()) || date.getFullYear() < 1900) return "—";
  return date.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "short",
  });
};

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function LoginScreen() {
  const { instance } = useMsal();
  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch(console.error);
  };
  return (
    <div className="login-screen">
      <div className="login-logo"><AdelanteLogo /></div>
      <div className="login-title">Aprobaciones</div>
      <div className="login-sub">Adelante Desarrollos · Business Central</div>
      <button className="btn-login" onClick={handleLogin}>
        <span>🔐</span>
        Ingresar con Microsoft
      </button>
    </div>
  );
}

const SORT_OPTIONS = [
  { id: "recent", label: "Recientes" },
  { id: "amount-desc", label: "Mayor ↓" },
  { id: "amount-asc", label: "Menor ↑" },
];

function sortOrders(orders, mode) {
  const copy = [...orders];
  if (mode === "amount-desc") {
    copy.sort(
      (a, b) => (b.totalAmountIncludingTax || 0) - (a.totalAmountIncludingTax || 0)
    );
  } else if (mode === "amount-asc") {
    copy.sort(
      (a, b) => (a.totalAmountIncludingTax || 0) - (b.totalAmountIncludingTax || 0)
    );
  }
  // "recent" is the default — BC already returns orderDate desc.
  return copy;
}

function OrdersList({
  orders,
  loading,
  error,
  actionLoading,
  onSelect,
  onReload,
  userName,
  onSwipeApprove,
  onSwipeReject,
  pendingId,
  locationsById,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onEnterSelection,
  onExitSelection,
}) {
  const [sortMode, setSortMode] = useState("recent");
  const visibleOrders = useMemo(
    () => (pendingId ? orders.filter((o) => o.id !== pendingId) : orders),
    [orders, pendingId]
  );
  const sortedOrders = useMemo(
    () => sortOrders(visibleOrders, sortMode),
    [visibleOrders, sortMode]
  );

  return (
    <div className={`app-shell ${selectionMode ? "selection-mode" : ""}`}>
      <div className="header">
        <div className="header-top">
          <div className="header-logo">
            <div className="header-logo-icon"><AdelanteLogo /></div>
            <div className="header-title">Aprobaciones</div>
            <span className={`env-badge env-badge-${ENV_IS_PROD ? "prod" : "sbx"}`}>
              {ENV_BADGE}
            </span>
          </div>
          <div className="header-user">
            {selectionMode ? (
              <button
                className="btn-multi active"
                onClick={onExitSelection}
                title="Salir de selección"
              >
                ✕
              </button>
            ) : (
              <>
                <button
                  className="btn-multi"
                  onClick={onEnterSelection}
                  title="Selección múltiple"
                  disabled={visibleOrders.length === 0}
                >
                  ☑
                </button>
                <button className="btn-reload" onClick={onReload} title="Actualizar">↻</button>
              </>
            )}
            <div className="avatar">{getInitials(userName)}</div>
          </div>
        </div>
        <div className="header-stats">
          {selectionMode ? (
            <>
              <span className="badge-count badge-count-selection">{selectedIds.size}</span>
              <span className="header-label">seleccionado{selectedIds.size === 1 ? "" : "s"}</span>
            </>
          ) : (
            <>
              <span className="badge-count">{loading ? "…" : visibleOrders.length}</span>
              <span className="header-label">pedidos pendientes</span>
            </>
          )}
        </div>
        {visibleOrders.length > 1 && (
          <div className="sort-bar" role="tablist" aria-label="Ordenar pedidos">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                role="tab"
                aria-selected={sortMode === opt.id}
                className={`sort-chip ${sortMode === opt.id ? "active" : ""}`}
                onClick={() => setSortMode(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <div className="loading-text">Cargando pedidos...</div>
        </div>
      ) : error ? (
        <div className="error-state">
          ⚠ {error}
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <div className="empty-title">Todo al día</div>
          <div className="empty-sub">No hay pedidos pendientes de aprobación</div>
        </div>
      ) : (
        <div className="orders-list">
          {sortedOrders.map((order, i) => {
            const wh = summarizeWarehouse(order.purchaseOrderLines, locationsById);
            const isSelected = selectedIds?.has(order.id);
            return (
              <SwipeableOrderCard
                key={order.id}
                className="order-card-shell"
                style={{ animationDelay: `${i * 0.05}s` }}
                disabled={actionLoading === order.number}
                disableSwipe={selectionMode}
                onTap={() =>
                  selectionMode ? onToggleSelect(order.id) : onSelect(order)
                }
                onSwipeRight={() => onSwipeApprove(order)}
                onSwipeLeft={() => onSwipeReject(order)}
              >
                <div className={`order-card ${isSelected ? "selected" : ""}`}>
                  {selectionMode && (
                    <span
                      className={`order-check ${isSelected ? "checked" : ""}`}
                      aria-hidden="true"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                  )}
                  <div className="order-card-content">
                    <div className="order-card-top">
                      <span className="order-number">{order.number}</span>
                      <span className="order-date">{fmtDate(order.orderDate)}</span>
                    </div>
                    <div className="order-vendor">{order.vendorName || "Proveedor"}</div>
                    <div className="order-card-bottom">
                      <div className="order-card-bottom-left">
                        <span className="order-amount">
                          {fmt(order.totalAmountIncludingTax, order.currencyCode)}
                        </span>
                        {wh && (
                          <span className={`wh-chip wh-chip-${wh.kind}`}>
                            {wh.label}
                          </span>
                        )}
                      </div>
                      {!selectionMode && <span className="chevron">›</span>}
                    </div>
                  </div>
                </div>
              </SwipeableOrderCard>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UndoToast({ pending, remainingMs, onUndo }) {
  if (!pending) return null;
  const label = pending.action === "approve" ? "Aprobando" : "Rechazando";
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return (
    <div className={`undo-toast undo-toast-${pending.action}`} role="status">
      <span className="undo-toast-label">
        {label} {pending.orderNumber} · {seconds}s
      </span>
      <button className="undo-toast-btn" onClick={onUndo}>Deshacer</button>
    </div>
  );
}

function AppContent() {
  const { accounts } = useMsal();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const {
    orders,
    loading,
    error,
    actionLoading,
    approve,
    reject,
    approveBulk,
    rejectBulk,
    fetchLines,
    reload,
    locationsById,
  } = useBCData();
  const userName = accounts[0]?.name || accounts[0]?.username || "";

  // Pending swipe action awaiting commit. Single-slot: if a new swipe arrives
  // while one is pending, we flush the existing one immediately and start the
  // new countdown.
  const [pending, setPending] = useState(null);
  const [pendingDeadline, setPendingDeadline] = useState(null);
  const [now, setNow] = useState(Date.now());
  const pendingRef = useRef(null);
  const timeoutRef = useRef(null);
  const [errorBanner, setErrorBanner] = useState(null);

  // Multi-select mode for bulk approve/reject.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkInProgress, setBulkInProgress] = useState(false);

  // Tick once a second so the toast countdown updates.
  useEffect(() => {
    if (!pending) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [pending]);

  const clearPending = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingRef.current = null;
    setPending(null);
    setPendingDeadline(null);
  };

  const commitPending = async (p) => {
    if (!p) return;
    const fn = p.action === "approve" ? approve : reject;
    const result = await fn(p.orderNumber);
    if (!result.success) {
      setErrorBanner(
        `No se pudo ${p.action === "approve" ? "aprobar" : "rechazar"} ${p.orderNumber}: ${result.error}`
      );
    }
  };

  const startPending = (order, action) => {
    // If there's already a pending action, commit it before starting the new one.
    const previous = pendingRef.current;
    clearPending();
    if (previous) commitPending(previous);

    const next = { orderId: order.id, orderNumber: order.number, action };
    pendingRef.current = next;
    setPending(next);
    setPendingDeadline(Date.now() + UNDO_WINDOW_MS);
    timeoutRef.current = setTimeout(() => {
      const p = pendingRef.current;
      clearPending();
      if (p) commitPending(p);
    }, UNDO_WINDOW_MS);
  };

  const handleUndo = () => {
    clearPending();
  };

  // If we navigate into the detail view, flush any pending action so the user
  // doesn't see a card we hid via swipe still affecting the underlying state.
  const openDetail = (order) => {
    const previous = pendingRef.current;
    clearPending();
    if (previous) commitPending(previous);
    setSelectedOrder(order);
  };

  const enterSelection = () => {
    // Flush any pending swipe undo before entering bulk mode so its card
    // doesn't reappear in the selection list.
    const previous = pendingRef.current;
    clearPending();
    if (previous) commitPending(previous);
    setSelectionMode(true);
    setSelectedIds(new Set());
  };

  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setBulkConfirm(null);
  };

  const toggleSelect = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  // Build a summary of the current selection for the confirmation modal.
  const selectionSummary = useMemo(() => {
    const selected = orders.filter((o) => selectedIds.has(o.id));
    const total = selected.reduce(
      (sum, o) => sum + (o.totalAmountIncludingTax || 0),
      0
    );
    const currencies = new Set(selected.map((o) => o.currencyCode || "CRC"));
    return {
      orders: selected,
      total,
      currency: currencies.size === 1 ? [...currencies][0] : null,
    };
  }, [orders, selectedIds]);

  const requestBulk = (action) => {
    if (selectionSummary.orders.length === 0) return;
    setBulkConfirm({ action });
  };

  const confirmBulk = async () => {
    if (!bulkConfirm) return;
    const action = bulkConfirm.action;
    const numbers = selectionSummary.orders.map((o) => o.number);
    setBulkInProgress(true);
    const fn = action === "approve" ? approveBulk : rejectBulk;
    const results = await fn(numbers);
    setBulkInProgress(false);
    setBulkConfirm(null);
    exitSelection();
    const failed = results.filter((r) => !r.success);
    if (failed.length > 0) {
      const ok = results.length - failed.length;
      setErrorBanner(
        `${ok} ${action === "approve" ? "aprobado" : "rechazado"}${ok === 1 ? "" : "s"}, ${failed.length} fallaron: ${failed.map((f) => f.orderNumber).join(", ")}`
      );
    }
  };

  if (selectedOrder) {
    return (
      <OrderDetail
        order={selectedOrder}
        onBack={() => setSelectedOrder(null)}
        onApprove={approve}
        onReject={reject}
        actionLoading={actionLoading}
        fetchLines={fetchLines}
        locationsById={locationsById}
      />
    );
  }

  const remainingMs = pendingDeadline ? Math.max(0, pendingDeadline - now) : 0;

  return (
    <>
      <OrdersList
        orders={orders}
        loading={loading}
        error={error}
        actionLoading={actionLoading}
        onSelect={openDetail}
        onReload={reload}
        userName={userName}
        onSwipeApprove={(o) => startPending(o, "approve")}
        onSwipeReject={(o) => startPending(o, "reject")}
        pendingId={pending?.orderId}
        locationsById={locationsById}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onEnterSelection={enterSelection}
        onExitSelection={exitSelection}
      />
      {!selectionMode && (
        <UndoToast pending={pending} remainingMs={remainingMs} onUndo={handleUndo} />
      )}
      {selectionMode && (
        <div className="bulk-footer" role="toolbar" aria-label="Acciones de selección múltiple">
          <div className="bulk-footer-summary">
            <span className="bulk-footer-count">
              {selectionSummary.orders.length}
            </span>
            <span className="bulk-footer-total">
              {selectionSummary.orders.length === 0
                ? "Toca pedidos para seleccionar"
                : fmt(selectionSummary.total, selectionSummary.currency || "CRC")}
            </span>
          </div>
          <div className="bulk-footer-actions">
            <button
              className="btn-bulk-reject"
              onClick={() => requestBulk("reject")}
              disabled={selectionSummary.orders.length === 0 || bulkInProgress}
            >
              ✗ Rechazar
            </button>
            <button
              className="btn-bulk-approve"
              onClick={() => requestBulk("approve")}
              disabled={selectionSummary.orders.length === 0 || bulkInProgress}
            >
              ✓ Aprobar
            </button>
          </div>
        </div>
      )}
      {bulkConfirm && (
        <div className="modal-overlay" onClick={() => !bulkInProgress && setBulkConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              ¿{bulkConfirm.action === "approve" ? "Aprobar" : "Rechazar"} {selectionSummary.orders.length}{" "}
              pedido{selectionSummary.orders.length === 1 ? "" : "s"}?
            </div>
            <div className="modal-sub">
              Total: {fmt(selectionSummary.total, selectionSummary.currency || "CRC")}
              {!selectionSummary.currency && " (monedas mixtas)"}
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setBulkConfirm(null)}
                disabled={bulkInProgress}
              >
                Cancelar
              </button>
              <button
                className={bulkConfirm.action === "approve" ? "btn-confirm-approve" : "btn-confirm-reject"}
                onClick={confirmBulk}
                disabled={bulkInProgress}
              >
                {bulkInProgress
                  ? "Procesando..."
                  : bulkConfirm.action === "approve"
                  ? "Confirmar aprobación"
                  : "Confirmar rechazo"}
              </button>
            </div>
          </div>
        </div>
      )}
      {errorBanner && (
        <div className="error-banner" role="alert">
          <span>{errorBanner}</span>
          <button onClick={() => setErrorBanner(null)} aria-label="Cerrar">×</button>
        </div>
      )}
    </>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  return isAuthenticated ? <AppContent /> : <LoginScreen />;
}
